import { z } from 'zod';
import { isValidYmd } from '../dates';
import { ACCOUNT_STATUSES, APP_STATUSES, RECORD_STATUSES } from '../enums';

/** Required trimmed text. */
export const text = (max: number, label = 'This field') =>
  z
    .string({ error: `${label} is required` })
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} must be at most ${max} characters`);

/**
 * Optional text: '' and null become null (clears the value on update);
 * undefined stays undefined (field not touched on update).
 */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be at most ${max} characters`)
    .nullish()
    .transform((v) => (v === '' ? null : v));

/** Entity id (UUID). */
export const id = z.uuid('Invalid id');

/** Required foreign key chosen in a form: empty → "<label> is required". */
export const requiredId = (label: string) =>
  z
    .string({ error: `${label} is required` })
    .min(1, `${label} is required`)
    .pipe(z.uuid('Invalid id'));

/** Optional foreign key: '' and null clear it, undefined leaves it unchanged. */
export const optionalId = z
  .union([z.literal(''), z.uuid('Invalid id')])
  .nullish()
  .transform((v) => (v === '' ? null : v));

/** 'YYYY-MM-DD' business date. */
export const ymd = z
  .string({ error: 'Date is required' })
  .refine((v) => isValidYmd(v), 'Invalid date (expected YYYY-MM-DD)');

/** Upper-case machine code such as GOOGLE_ADS. */
export const code = (max = 40) =>
  z
    .string({ error: 'Code is required' })
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_]+$/, 'Use only letters, digits and underscores')
    .min(2, 'Code must be at least 2 characters')
    .max(max);

export const currencyCode = z
  .string({ error: 'Currency is required' })
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Use a 3-letter ISO currency code');

export const recordStatus = z.enum(RECORD_STATUSES);
export const appStatus = z.enum(APP_STATUSES);
export const accountStatus = z.enum(ACCOUNT_STATUSES);

/** Accept number or string input; always produce a canonical decimal string. */
const decimalInput = z.union([z.string(), z.number()]).transform((v) => String(v).trim());

/** Non-negative money amount with up to 4 decimal places (DECIMAL(18,4)). */
export const moneyAmount = decimalInput.pipe(
  z
    .string()
    .min(1, 'Amount is required')
    .regex(/^\d{1,14}(\.\d{1,4})?$/, 'Enter a non-negative amount with at most 4 decimals'),
);

/** GST rate percentage 0–100 with up to 2 decimal places. */
export const gstRatePercent = decimalInput.pipe(
  z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter a rate with at most 2 decimals')
    .refine((v) => Number(v) <= 100, 'Rate cannot exceed 100%'),
);

/** Exchange rate (> 0) with up to 6 decimal places. */
export const exchangeRate = decimalInput.pipe(
  z
    .string()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Enter a rate with at most 6 decimals')
    .refine((v) => Number(v) > 0, 'Rate must be greater than zero'),
);

export const sortOrder = z.coerce.number().int().min(0).max(100_000);

/** Optional email: '' / null clear it; stored lower-case. */
export const optionalEmail = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim().toLowerCase()) : v),
  z.email('Enter a valid email address').max(191).nullish(),
);

/** Form inputs send '' for "nothing chosen"; treat it as null (clear) for optional values. */
export const emptyAsNull = <T extends z.ZodType>(schema: T) =>
  z.preprocess((v) => (v === '' ? null : v), schema.nullish());

/** Treat '' as "not provided" (server applies its default). */
export const emptyAsUndefined = <T extends z.ZodType>(schema: T) =>
  z.preprocess((v) => (v === '' || v === null ? undefined : v), schema.optional());

/** Optimistic concurrency token sent back with updates. */
export const concurrency = {
  updatedAt: z.iso.datetime({ offset: true }).optional(),
};

export const listQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(25),
  sort: z
    .string()
    .regex(/^-?[a-zA-Z]+$/)
    .optional(),
  search: z.string().trim().max(100).optional(),
});
export type ListQuery = z.output<typeof listQuery>;

export const statusFilter = z.object({ status: recordStatus.optional() });
